import React, { useContext, useEffect, useRef, useState } from "react"
import { FC } from "react"
import { Card, Col, Flex, Radio, Row, Space, Tooltip, Typography } from "antd"
import { AppMetierTitle } from "../../components/AppMetierTitle/AppMetierTitle"
import { AppMetierSwitch } from "../../components/AppMetierSwitch"
import { CodeLibelle } from "../../client/backend-client/generated"
import { ProjectContext } from "../ProjectWrapper"
import { useDebouncedCallback } from "use-debounce"
import { getFlushEffectCallback, UPDATE_DEBOUNCE_DEFAULT_DELAY } from "../../utils/DebounceUtils"
import { StarOutlined } from "@ant-design/icons"
import { RadioChangeEvent } from "antd/lib"
import { useUpdateProjectSector, useUpdateStructureSector } from "../../queries/SectorsQueries"
import { Filieres } from "../../client/backend-client/filieres"

const { Link, Paragraph, Text } = Typography

export const SectorsForm: FC<{
  sectors: Filieres[]
  structureSector: CodeLibelle | undefined
  projectSector: CodeLibelle | undefined
}> = ({
  sectors: filieres,
  structureSector: regisStructureSector,
  projectSector: noubaProjectSector
}) => {
  const refs = useRef({
    initialStructureSector: regisStructureSector,
    initialProjectSector: noubaProjectSector
  })

  const [structureSector, setStructureSector] = useState<CodeLibelle | undefined>(
    refs.current.initialStructureSector
  )
  const [projectSector, setProjectSector] = useState<CodeLibelle | undefined>(
    refs.current.initialProjectSector
  )

  const isProjectSectorDifferentFromStructureSector = () => {
    return (
      structureSector !== null &&
      projectSector !== null &&
      projectSector?.code !== structureSector?.code
    )
  }

  const [defineProjectSector, setDefineProjectSector] = useState<boolean>(
    isProjectSectorDifferentFromStructureSector()
  )

  const { structureId, projectId } = useContext(ProjectContext)

  const updateStructureSector = useUpdateStructureSector(structureId, projectId)
  const updateProjectSector = useUpdateProjectSector(projectId)

  const updateStructureSectorData = (data: CodeLibelle) => {
    if (data !== refs.current.initialStructureSector) {
      updateStructureSector.mutate(data, {
        onSuccess: res => {
          refs.current.initialStructureSector = res
        }
      })
    }
  }

  const updateProjectSectorData = (data: CodeLibelle) => {
    if (data !== refs.current.initialProjectSector) {
      updateProjectSector.mutate(data, {
        onSuccess: res => {
          refs.current.initialProjectSector = res
        }
      })
    }
  }

  const debouncedOnStructureSectorChange = useDebouncedCallback(() => {
    if (structureSector) updateStructureSectorData(structureSector)
  }, UPDATE_DEBOUNCE_DEFAULT_DELAY)

  useEffect(debouncedOnStructureSectorChange, [structureSector])
  useEffect(getFlushEffectCallback(debouncedOnStructureSectorChange), [
    debouncedOnStructureSectorChange
  ])

  const debouncedOnProjectSectorChange = useDebouncedCallback(() => {
    if (projectSector) updateProjectSectorData(projectSector)
  }, UPDATE_DEBOUNCE_DEFAULT_DELAY)

  useEffect(debouncedOnProjectSectorChange, [projectSector])
  useEffect(getFlushEffectCallback(debouncedOnProjectSectorChange), [
    debouncedOnProjectSectorChange
  ])

  return (
    <Card>
      <AppMetierTitle level={2}>Référencement des projets par filières </AppMetierTitle>
      <Paragraph>
        Il s'agit de référencer dans quelle filière s'inscrivent la structure et le projet : 
      </Paragraph>
      <Paragraph>
        <ul>
          <li>
            <Text strong>Pour une structure en création</Text>, la filière de la structure et du projet sont identiques.{" "}
            <Text strong>Il s'agit de saisir uniquement la filière de la structure,</Text> celle du projet est par défaut la même (référencement automatique).
          </li>
          <li>
            <Text strong>Pour une structure existante, les filières peuvent être différentes. Si c'est le cas, la filière du projet peut être modifiée.</Text>
          </li>
        </ul>
      </Paragraph>
      <Paragraph>
        <Text strong>Par exemple,</Text> une SIAE (saisie de la filière de la structure  = Solidarité, inclusion, cohésion sociale) a un projet de diversification avec une{" "}
        activité de transformation alimentaire durable (saisie de la filière du projet = Agriculture et alimentation durable).
      </Paragraph>
      <Paragraph>
        <ul>
          <li>
            <Text strong>Pour une structure existante, les filières peuvent être identiques. Il n'est alors pas nécessaire de modifier la filière du projet.</Text>
          </li>
        </ul>
      </Paragraph>
      <Paragraph>
        <Text strong>Par exemple,</Text> une SIAE (saisie de la filière de la structure = Filière Solidarité, inclusion, cohésion sociale) a un projet de développement global{" "} 
        de la structure (la filière du projet est toujours Solidarité, inclusion, cohésion sociale).
      </Paragraph>
      <Paragraph>
        <Text strong>Si la structure ne correspond à aucune filière prioritaire,</Text>{" "}
        elle sera référencée dans{" "}
        <Text strong>"hors filière prioritaire".</Text>
      </Paragraph>
      <Paragraph>
        <Text strong>Pour comprendre l'objectif de ce référencement et avoir une liste détaillée des activités par filières prioritaires :</Text>{" "}
        <Link href={"https://fract.sharepoint.com/sites/NINO-Offre-investissement-solidaire/SiteAssets/Forms/AllItems.aspx?id=%2fsites%2fNINO%2DOffre%2Dinvestissement%2Dsolidaire%2fSiteAssets%2fSitePages%2fcjg78vzk%2fFiche%2Dpratique%2Dm%C3%A9tier%5FR%C3%A9f%C3%A9rencement%2Ddes%2Dprojets%2Dpar%2Dfili%C3%A8res%2D1%2Epdf&parent=%2fsites%2fNINO%2DOffre%2Dinvestissement%2Dsolidaire%2fSiteAssets%2fSitePages%2fcjg78vzk"} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
          Fiche pratique_Référencement des projets par filières
        </Link>        
      </Paragraph>
      <Space />
      <Row>
        <Col span={8}>
          <Flex align="center" gap="large">
            <AppMetierTitle level={5}>Choisir la filière de la structure</AppMetierTitle>
            {structureSector?.code === undefined && (
              <Tooltip title="Le choix d'une filière structure est obligatoire">
                <StarOutlined style={{ color: "red", paddingBottom: "0.5em" }} />
              </Tooltip>
            )}
          </Flex>
        </Col>
        <Col span={8}>
          <AppMetierSwitch
            label={"La filière du projet est différente"}
            checked={defineProjectSector}
            style={{ color: "var(--main-green-1)", paddingTop: "0.2em" }}
            onChange={() => {
              if (structureSector !== undefined) {
                setProjectSector(structureSector)
              }
              setDefineProjectSector(!defineProjectSector)
            }}
          ></AppMetierSwitch>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <SectorsPanel
            sectors={filieres}
            selectedSector={structureSector}
            onChange={evt =>
              setStructureSector(filieres.find(ref => ref.code === evt.target.value))
            }
          />
        </Col>
        {defineProjectSector && (
          <Col span={8}>
            <SectorsPanel
              sectors={filieres}
              selectedSector={projectSector}
              onChange={evt =>
                setProjectSector(filieres.find(ref => ref.code === evt.target.value))
              }
            />
          </Col>
        )}
      </Row>
    </Card>
  )
}

const SectorsPanel: FC<{
  sectors: Filieres[]
  selectedSector: CodeLibelle | undefined
  onChange: (evt: RadioChangeEvent) => void
}> = ({ sectors, selectedSector, onChange }) => (
  <Radio.Group
    optionType="default"
    buttonStyle="outline"
    defaultValue={selectedSector?.code}
    onChange={evt => onChange(evt)}
  >
    {sectors
      .sort((a: Filieres, b: Filieres) => parseInt(a.ordre) - parseInt(b.ordre))
      .map(filiere => (
        <Row key={filiere.ordre}>
          <Col span={2}>
            <Radio.Button value={filiere.code} />
          </Col>
          <Col span={22}>
            <Tooltip title={filiere.description}>
              <Paragraph>{filiere.libelle}</Paragraph>
            </Tooltip>
          </Col>
          <Col></Col>
        </Row>
      ))}
  </Radio.Group>
)
